import { useEffect, useRef, useState } from "react";

import { designate } from "../../components/attention";
import { Dialog, DialogBody } from "../../components/Dialog";
import { Button } from "../../components/ui";
import { t } from "../../i18n/i18n";
import { useUi } from "../../state/ui";

// ui-check: ignore-start — encre de la signature : noire comme sur le PDF et l'iPad (contenu du document, pas
// une couleur d'interface).
const SIGNATURE_INK = "#000";
// ui-check: ignore-end

interface Point {
  x: number;
  y: number;
}

/**
 * Signature manuscrite, comme sur l'iPad (SignatureCapturePopupView) : traits noirs de 3 points, bouts ronds ;
 * l'image enregistrée est le cadre des traits plus 5 pixels, sur fond transparent.
 */
export function SignaturePad({
  hasSignature,
  onSave,
  onDelete,
  onClose,
}: {
  hasSignature: boolean;
  onSave: (pngBase64: string) => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const notify = useUi((st) => st.notify);
  const canvas = useRef<HTMLCanvasElement>(null);
  const strokes = useRef<Point[][]>([]);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const el = canvas.current;
    if (!el) return;
    const ratio = window.devicePixelRatio || 1;
    el.width = el.clientWidth * ratio;
    el.height = el.clientHeight * ratio;
    const ctx = el.getContext("2d");
    ctx?.scale(ratio, ratio);
  }, []);

  const point = (e: React.PointerEvent<HTMLCanvasElement>): Point => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const redraw = () => {
    const el = canvas.current;
    const ctx = el?.getContext("2d");
    if (!el || !ctx) return;
    ctx.clearRect(0, 0, el.width, el.height);
    ctx.strokeStyle = SIGNATURE_INK;
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    for (const s of strokes.current) {
      ctx.beginPath();
      s.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
      const first = s[0];
      if (s.length === 1 && first) ctx.lineTo(first.x + 0.1, first.y);
      ctx.stroke();
    }
  };

  const exportPng = (): string | null => {
    const all = strokes.current.flat();
    if (!all.length) return null;
    const pad = 5;
    const minX = Math.min(...all.map((p) => p.x)) - pad;
    const minY = Math.min(...all.map((p) => p.y)) - pad;
    const w = Math.max(...all.map((p) => p.x)) + pad - minX;
    const h = Math.max(...all.map((p) => p.y)) + pad - minY;
    if (w < 5 || h < 5) return null;
    const ratio = window.devicePixelRatio || 1;
    const out = document.createElement("canvas");
    out.width = Math.ceil(w * ratio);
    out.height = Math.ceil(h * ratio);
    const ctx = out.getContext("2d");
    if (!ctx) return null;
    ctx.scale(ratio, ratio);
    ctx.translate(-minX, -minY);
    ctx.strokeStyle = SIGNATURE_INK;
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    for (const s of strokes.current) {
      ctx.beginPath();
      s.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
      const first = s[0];
      if (s.length === 1 && first) ctx.lineTo(first.x + 0.1, first.y);
      ctx.stroke();
    }
    return out.toDataURL("image/png");
  };

  // Fenêtre partagée (R9) : voile, Échap, focus gardé dans la fenêtre.
  return (
    <Dialog
      title={t("Signature de l'autorité")}
      onClose={onClose}
      width={560}
      footer={
        <>
          <div className="mr-auto flex gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                strokes.current = [];
                setEmpty(true);
                redraw();
              }}
            >
              {t("Effacer")}
            </Button>
            {hasSignature && (
              <Button variant="danger-ghost" onClick={onDelete}>
                {t("Supprimer la signature")}
              </Button>
            )}
          </div>
          <Button variant="secondary" onClick={onClose}>
            {t("Annuler")}
          </Button>
          <Button
            blocked={empty}
            onBlocked={() => {
              // Rien n'est dessiné : la zone de signature est l'action attendue (R12).
              designate(canvas.current, "ring");
              notify({ text: t("Dessinez votre signature"), tone: "info" });
            }}
            onClick={() => {
              const png = exportPng();
              if (png) onSave(png);
            }}
          >
            {t("Valider la signature")}
          </Button>
        </>
      }
    >
      <DialogBody>
        <p className="text-ui text-ink-soft">{t("Dessinez votre signature")}</p>
        <div className="relative">
          <canvas
            ref={canvas}
            className="h-[200px] w-full touch-none rounded-lg border border-line-strong bg-white"
            onPointerDown={(e) => {
              e.currentTarget.setPointerCapture(e.pointerId);
              drawing.current = true;
              strokes.current.push([point(e)]);
              setEmpty(false);
              redraw();
            }}
            onPointerMove={(e) => {
              if (!drawing.current) return;
              strokes.current[strokes.current.length - 1]?.push(point(e));
              redraw();
            }}
            onPointerUp={() => (drawing.current = false)}
          />
          {empty && (
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-title text-ink-muted">
              {t("Signez ici")}
            </span>
          )}
        </div>
      </DialogBody>
    </Dialog>
  );
}
